import { useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { FEATURED_EVENT_SLUG } from "../features/home/data.ts";

// Mirrors the restomag.com top promo bar (restomag-website EventAnnouncementBar) for the
// Eye Kandy Kustoms Open House — here it links to the event's own entry page (same domain).
const DISMISS_KEY = "event-bar-dismissed-v1";
const EVENT_PATH = `/${FEATURED_EVENT_SLUG}`;

// The Nav and page content are both offset by this fixed bar's height via a CSS variable, so
// they drop below it while it's shown and collapse back to 0 once it's dismissed/hidden.
const BAR_H_VAR = "--ann-bar-h";

/**
 * Thin dismissible promo strip for the featured event. Rendered once in the root layout, so it
 * sits above every screen. Hidden on the event's own page (self-referential) and in kiosk mode.
 */
export function EventAnnouncementBar() {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const isKiosk = params.get("kiosk") === "1";
  const onEventPage = pathname === EVENT_PATH;

  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem(DISMISS_KEY) === "1",
  );

  // GUARD — single-event assumption: this promotes the one featured (RestoMag-operated)
  // event and currently shows on every page except that event's own funnel. Before a
  // SECOND / third-party event goes live, gate this to first-party marketing routes only
  // (e.g. a "/" + "/host" allowlist) — otherwise it advertises Eye Kandy on a tenant's page.
  const visible = !dismissed && !isKiosk && !onEventPage;
  const ref = useRef<HTMLDivElement>(null);

  // Publish the bar's height so the fixed Nav (top) and page content (padding-top) can clear it;
  // re-measure on resize (the copy can wrap on narrow screens), and reset to 0 when not shown.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!visible) {
      root.style.setProperty(BAR_H_VAR, "0px");
      return;
    }
    const apply = () => root.style.setProperty(BAR_H_VAR, `${ref.current?.offsetHeight ?? 0}px`);
    apply();
    window.addEventListener("resize", apply);
    return () => {
      window.removeEventListener("resize", apply);
      root.style.setProperty(BAR_H_VAR, "0px");
    };
  }, [visible]);

  if (!visible) return null;

  function dismiss() {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  }

  return (
    <div
      ref={ref}
      className="fixed inset-x-0 top-0 z-[210] flex items-center justify-center gap-3 bg-gradient-to-r from-flame-dark via-flame to-flame-dark px-10 py-2 text-center text-sm font-medium text-white"
    >
      <Link to={EVENT_PATH} className="inline-flex items-center gap-2 no-underline hover:underline">
        🏁 RestoMag × Eye Kandy Kustoms Open House — Jul 1, Vancouver
        <span aria-hidden>→</span>
      </Link>
      <button
        type="button"
        onClick={dismiss}
        aria-label="Dismiss announcement"
        className="absolute right-3 flex h-6 w-6 items-center justify-center rounded-full text-white/80 hover:bg-white/15 hover:text-white"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
      </button>
    </div>
  );
}
